/**
 * Route Feature Flags — runtime kill-switches for MVP vs non-MVP routes.
 *
 * Flags live in the `route_flags` table so they can be flipped from the admin
 * console without a redeploy. The values below are the compile-time fallbacks
 * used before the table loads (and if the fetch fails), so the app never blanks.
 */

export type RouteFlagKey =
  | "route_bgmi"
  | "route_free_fire"
  | "route_checkout"
  | "route_credentials"
  | "route_capstone"
  | "route_frameworks"
  | "route_rewards";

export type RouteFlagGroup = "mvp" | "non_mvp";

export interface RouteFlagDefinition {
  key: RouteFlagKey;
  label: string;
  description: string;
  group: RouteFlagGroup;
  /** Fallback value used until DB flags load. */
  defaultEnabled: boolean;
  /** Where to send visitors when the route is disabled. */
  fallbackPath: string;
}

export const ROUTE_FLAGS: Record<RouteFlagKey, RouteFlagDefinition> = {
  route_bgmi: {
    key: "route_bgmi",
    label: "BGMI Rank-Up Path",
    description: "Public BGMI landing page and pricing.",
    group: "mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_free_fire: {
    key: "route_free_fire",
    label: "Free Fire Rank-Up Path",
    description: "Public Free Fire pathway entry point.",
    group: "mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_checkout: {
    key: "route_checkout",
    label: "Pathway checkout",
    description: "Razorpay checkout route for pathway purchases.",
    group: "mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_credentials: {
    key: "route_credentials",
    label: "Credential pages",
    description: "Public certificate and credential verification pages.",
    group: "mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_capstone: {
    key: "route_capstone",
    label: "Pathway capstone",
    description: "Capstone submission route for enrolled learners.",
    group: "non_mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_frameworks: {
    key: "route_frameworks",
    label: "Frameworks & alignment",
    description: "Public frameworks index and coverage explorer.",
    group: "non_mvp",
    defaultEnabled: true,
    fallbackPath: "/learn",
  },
  route_rewards: {
    key: "route_rewards",
    label: "Rewards & GG Points",
    description: "Learner rewards surface.",
    group: "non_mvp",
    defaultEnabled: true,
    fallbackPath: "/app/home",
  },
};

export const ROUTE_FLAG_KEYS = Object.keys(ROUTE_FLAGS) as RouteFlagKey[];

export const DEFAULT_ROUTE_FLAGS: Record<RouteFlagKey, boolean> = ROUTE_FLAG_KEYS.reduce(
  (acc, key) => {
    acc[key] = ROUTE_FLAGS[key].defaultEnabled;
    return acc;
  },
  {} as Record<RouteFlagKey, boolean>,
);
